import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check, Pencil, Plus, Tags, X } from 'lucide-react';
import api from '../../lib/api';
import { kolUrlSegment } from '../../lib/slug';
import { KOLProfile, KolGender, Tag } from '../../types';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Modal from '../../components/ui/Modal';
import TagSelector from '../../components/ui/TagSelector';

const PAGE_SIZE = 100;

const GENDER_OPTIONS: { value: '' | KolGender; label: string }[] = [
  { value: '', label: '—' },
  { value: 'FEMALE', label: 'Female' },
  { value: 'MALE', label: 'Male' },
  { value: 'NON_BINARY', label: 'Non-binary' },
  { value: 'OTHER', label: 'Other' },
  { value: 'PREFER_NOT_TO_SAY', label: 'Prefer not to say' },
];

type ListResponse = { items: KOLProfile[]; total: number };

type NewKolDraft = {
  name: string;
  email: string;
  phone: string;
  locationCity: string;
  ethnicity: string;
  gender: KolGender | '';
  instagramHandle: string;
  instagramFollowers: string;
  tiktokHandle: string;
  tiktokFollowers: string;
  youtubeChannel: string;
  youtubeFollowers: string;
  facebookFollowers: string;
  wechatId: string;
};

function emptyNewKolDraft(): NewKolDraft {
  return {
    name: '',
    email: '',
    phone: '',
    locationCity: '',
    ethnicity: '',
    gender: '',
    instagramHandle: '',
    instagramFollowers: '',
    tiktokHandle: '',
    tiktokFollowers: '',
    youtubeChannel: '',
    youtubeFollowers: '',
    facebookFollowers: '',
    wechatId: '',
  };
}

function validateNewKolDraft(draft: NewKolDraft): string | null {
  if (!draft.name.trim()) return 'Name is required before saving.';
  if (!draft.email.trim()) return 'Email is required before saving.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email.trim())) {
    return 'Enter a valid email address before saving.';
  }
  return null;
}

function parseOptionalInt(value: string): number | null {
  const t = value.trim();
  if (!t) return null;
  const n = parseInt(t, 10);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function draftInputClass(invalid?: boolean) {
  return `w-full min-w-[6rem] px-2 py-1 text-sm bg-glass-2 border rounded text-glass-text ${
    invalid ? 'border-rose-500/60' : 'border-glass'
  }`;
}

function normalizeListResponse(data: unknown): ListResponse {
  if (Array.isArray(data)) {
    return { items: data as KOLProfile[], total: data.length };
  }
  const o = data as { items?: KOLProfile[]; total?: number };
  return {
    items: Array.isArray(o.items) ? o.items : [],
    total: typeof o.total === 'number' ? o.total : 0,
  };
}

function EditableTextCell({
  value,
  disabled,
  placeholder,
  onCommit,
  className = '',
}: {
  value: string | null | undefined;
  disabled?: boolean;
  placeholder?: string;
  onCommit: (next: string | undefined) => void;
  className?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => (value ?? '').toString());

  useEffect(() => {
    if (!editing) setDraft((value ?? '').toString());
  }, [value, editing]);

  const display = (value ?? '').toString().trim() || '—';

  if (disabled) {
    return <span className={`text-sm text-glass-text-2 px-2 py-1 ${className}`}>{display}</span>;
  }

  if (!editing) {
    return (
      <button
        type="button"
        className={`text-left text-sm text-glass-text px-2 py-1 rounded hover:bg-glass-3 w-full min-h-[2rem] ${className}`}
        onClick={() => setEditing(true)}
      >
        {display}
      </button>
    );
  }

  return (
    <input
      autoFocus
      className={`w-full min-w-[6rem] px-2 py-1 text-sm bg-glass-2 border border-glass rounded text-glass-text ${className}`}
      value={draft}
      placeholder={placeholder}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        setEditing(false);
        const trimmed = draft.trim();
        const next = trimmed === '' ? undefined : trimmed;
        const prev = (value ?? '').toString().trim();
        const prevNorm = prev === '' ? undefined : prev;
        if (next !== prevNorm) onCommit(next);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') {
          setDraft((value ?? '').toString());
          setEditing(false);
        }
      }}
    />
  );
}

function EditableNumberCell({
  value,
  disabled,
  onCommit,
  className = '',
}: {
  value: number | null | undefined;
  disabled?: boolean;
  onCommit: (next: number | null) => void;
  className?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() =>
    value != null && Number.isFinite(value) ? String(value) : ''
  );

  useEffect(() => {
    if (!editing) {
      setDraft(value != null && Number.isFinite(value) ? String(value) : '');
    }
  }, [value, editing]);

  const display =
    value != null && Number.isFinite(value) ? Number(value).toLocaleString() : '—';

  if (disabled) {
    return <span className={`text-sm text-glass-text-2 px-2 py-1 tabular-nums ${className}`}>{display}</span>;
  }

  if (!editing) {
    return (
      <button
        type="button"
        className={`text-left text-sm text-glass-text px-2 py-1 rounded hover:bg-glass-3 w-full min-h-[2rem] tabular-nums ${className}`}
        onClick={() => setEditing(true)}
      >
        {display}
      </button>
    );
  }

  return (
    <input
      type="number"
      min={0}
      autoFocus
      className={`w-full min-w-[5rem] px-2 py-1 text-sm bg-glass-2 border border-glass rounded text-glass-text tabular-nums ${className}`}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        setEditing(false);
        const t = draft.trim();
        if (t === '') {
          if (value != null) onCommit(null);
          return;
        }
        const n = parseInt(t, 10);
        if (!Number.isFinite(n) || n < 0) return;
        if (n !== (value ?? null)) onCommit(n);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') {
          setDraft(value != null && Number.isFinite(value) ? String(value) : '');
          setEditing(false);
        }
      }}
    />
  );
}

export default function KOLsTableEdit() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.get('/auth/me').then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const agencyOrgId = useMemo(() => {
    const m = meQuery.data?.memberships?.find(
      (x: { organization?: { type?: string } }) => x.organization?.type === 'AGENCY'
    );
    return m?.organizationId ?? '';
  }, [meQuery.data]);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, tagFilter]);

  const tagsQuery = useQuery({
    queryKey: ['tags'],
    queryFn: () => api.get<Tag[]>('/tags').then((r) => r.data),
    staleTime: 10 * 60 * 1000,
  });

  const listQuery = useQuery({
    queryKey: ['agency', 'kols-table', agencyOrgId, page, debouncedSearch, tagFilter],
    queryFn: async () => {
      const skip = (page - 1) * PAGE_SIZE;
      const params: Record<string, string | number> = {
        skip,
        take: PAGE_SIZE,
      };
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (tagFilter) params.tags = tagFilter;
      const res = await api.get('/kols', { params });
      return normalizeListResponse(res.data);
    },
    enabled: Boolean(agencyOrgId),
    staleTime: 30 * 1000,
  });

  const patchMutation = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) => {
      await api.put(`/kols/${id}`, body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['agency', 'kols-table'] });
      void queryClient.invalidateQueries({ queryKey: ['agency', 'kols'] });
    },
    onError: (err: unknown) => {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      alert(msg || 'Save failed');
    },
  });

  const [tagsModalKol, setTagsModalKol] = useState<KOLProfile | null>(null);
  const [tagsDraft, setTagsDraft] = useState<string[]>([]);
  const [newRowDraft, setNewRowDraft] = useState<NewKolDraft | null>(null);
  const [newRowError, setNewRowError] = useState<string | null>(null);
  const [newRowTouched, setNewRowTouched] = useState(false);

  const createMutation = useMutation({
    mutationFn: async (draft: NewKolDraft) => {
      const body: Record<string, unknown> = {
        agencyOrgId,
        name: draft.name.trim(),
        email: draft.email.trim(),
        phone: draft.phone.trim() || undefined,
        locationCity: draft.locationCity.trim() || undefined,
        ethnicity: draft.ethnicity.trim() || undefined,
        gender: draft.gender === '' ? undefined : draft.gender,
        instagramHandle: draft.instagramHandle.trim() || undefined,
        tiktokHandle: draft.tiktokHandle.trim() || undefined,
        youtubeChannel: draft.youtubeChannel.trim() || undefined,
        wechatId: draft.wechatId.trim() || undefined,
      };
      const ig = parseOptionalInt(draft.instagramFollowers);
      const tt = parseOptionalInt(draft.tiktokFollowers);
      const yt = parseOptionalInt(draft.youtubeFollowers);
      const fb = parseOptionalInt(draft.facebookFollowers);
      if (ig != null) body.instagramFollowers = ig;
      if (tt != null) body.tiktokFollowers = tt;
      if (yt != null) body.youtubeFollowers = yt;
      if (fb != null) body.facebookFollowers = fb;
      await api.post('/kols', body);
    },
    onSuccess: () => {
      setNewRowDraft(null);
      setNewRowError(null);
      setNewRowTouched(false);
      void queryClient.invalidateQueries({ queryKey: ['agency', 'kols-table'] });
      void queryClient.invalidateQueries({ queryKey: ['agency', 'kols'] });
    },
    onError: (err: unknown) => {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      setNewRowError(msg || 'Failed to create KOL');
    },
  });

  const openTagsModal = (kol: KOLProfile) => {
    setTagsModalKol(kol);
    setTagsDraft(
      (kol.tags ?? []).map((t: { tagId?: string; tag?: { id: string } }) => t.tagId || t.tag?.id).filter(Boolean) as string[]
    );
  };

  const saveTags = async () => {
    if (!tagsModalKol) return;
    try {
      await api.put(`/kols/${tagsModalKol.id}`, { tagIds: tagsDraft });
      setTagsModalKol(null);
      void queryClient.invalidateQueries({ queryKey: ['agency', 'kols-table'] });
      void queryClient.invalidateQueries({ queryKey: ['agency', 'kols'] });
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      alert(msg || 'Failed to save tags');
    }
  };

  const total = listQuery.data?.total ?? 0;
  const items = listQuery.data?.items ?? [];
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const commitField = (id: string, body: Record<string, unknown>) => {
    patchMutation.mutate({ id, body });
  };

  const tags = tagsQuery.data ?? [];

  const startNewRow = () => {
    setNewRowDraft(emptyNewKolDraft());
    setNewRowError(null);
    setNewRowTouched(false);
  };

  const cancelNewRow = () => {
    setNewRowDraft(null);
    setNewRowError(null);
    setNewRowTouched(false);
  };

  const saveNewRow = () => {
    if (!newRowDraft) return;
    setNewRowTouched(true);
    const validationError = validateNewKolDraft(newRowDraft);
    if (validationError) {
      setNewRowError(validationError);
      return;
    }
    setNewRowError(null);
    createMutation.mutate(newRowDraft);
  };

  const updateNewRowField = <K extends keyof NewKolDraft>(key: K, value: NewKolDraft[K]) => {
    setNewRowDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
    if (newRowError) setNewRowError(null);
  };

  const showTable = agencyOrgId && (!listQuery.isPending || newRowDraft);
  const nameInvalid = newRowTouched && !newRowDraft?.name.trim();
  const emailInvalid =
    newRowTouched &&
    (!newRowDraft?.email.trim() ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newRowDraft.email.trim()));

  const tableHeader = (
    <thead className="bg-glass-1 sticky top-0 z-10">
      <tr>
        <th className="px-2 py-2 text-left text-[0.65rem] font-medium uppercase tracking-wider text-glass-text-2">
          Profile
        </th>
        <th className="px-2 py-2 text-left text-[0.65rem] font-medium uppercase tracking-wider text-glass-text-2">
          Name
        </th>
        <th className="px-2 py-2 text-left text-[0.65rem] font-medium uppercase tracking-wider text-glass-text-2">
          Email
        </th>
        <th className="px-2 py-2 text-left text-[0.65rem] font-medium uppercase tracking-wider text-glass-text-2">
          Phone
        </th>
        <th className="px-2 py-2 text-left text-[0.65rem] font-medium uppercase tracking-wider text-glass-text-2">
          Location
        </th>
        <th className="px-2 py-2 text-left text-[0.65rem] font-medium uppercase tracking-wider text-glass-text-2">
          Ethnicity
        </th>
        <th className="px-2 py-2 text-left text-[0.65rem] font-medium uppercase tracking-wider text-glass-text-2 w-[8rem]">
          Gender
        </th>
        <th className="px-2 py-2 text-left text-[0.65rem] font-medium uppercase tracking-wider text-glass-text-2">
          IG @
        </th>
        <th className="px-2 py-2 text-left text-[0.65rem] font-medium uppercase tracking-wider text-glass-text-2">
          IG followers
        </th>
        <th className="px-2 py-2 text-left text-[0.65rem] font-medium uppercase tracking-wider text-glass-text-2">
          TikTok @
        </th>
        <th className="px-2 py-2 text-left text-[0.65rem] font-medium uppercase tracking-wider text-glass-text-2">
          TT followers
        </th>
        <th className="px-2 py-2 text-left text-[0.65rem] font-medium uppercase tracking-wider text-glass-text-2">
          YouTube
        </th>
        <th className="px-2 py-2 text-left text-[0.65rem] font-medium uppercase tracking-wider text-glass-text-2">
          YT subs
        </th>
        <th className="px-2 py-2 text-left text-[0.65rem] font-medium uppercase tracking-wider text-glass-text-2">
          FB followers
        </th>
        <th className="px-2 py-2 text-left text-[0.65rem] font-medium uppercase tracking-wider text-glass-text-2">
          WeChat ID
        </th>
        <th className="px-2 py-2 text-left text-[0.65rem] font-medium uppercase tracking-wider text-glass-text-2">
          Tags
        </th>
      </tr>
    </thead>
  );

  const newRowCells = newRowDraft ? (
    <tr className="bg-accent2/10 border-b border-accent2/30">
      <td className="px-2 py-1 whitespace-nowrap">
        <div className="flex items-center gap-1">
          <Button
            type="button"
            size="sm"
            className="text-xs h-7 px-2"
            onClick={saveNewRow}
            disabled={createMutation.isPending}
          >
            <Check className="w-3.5 h-3.5 mr-1" />
            Save
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="text-xs h-7 px-2"
            onClick={cancelNewRow}
            disabled={createMutation.isPending}
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      </td>
      <td className="px-2 py-1 min-w-[7rem]">
        <input
          autoFocus
          className={draftInputClass(nameInvalid)}
          value={newRowDraft.name}
          placeholder="Name *"
          onChange={(e) => updateNewRowField('name', e.target.value)}
        />
      </td>
      <td className="px-2 py-1 min-w-[10rem]">
        <input
          className={draftInputClass(emailInvalid)}
          value={newRowDraft.email}
          placeholder="Email *"
          onChange={(e) => updateNewRowField('email', e.target.value)}
        />
      </td>
      <td className="px-2 py-1 min-w-[7rem]">
        <input
          className={draftInputClass()}
          value={newRowDraft.phone}
          placeholder="Phone"
          onChange={(e) => updateNewRowField('phone', e.target.value)}
        />
      </td>
      <td className="px-2 py-1 min-w-[7rem]">
        <input
          className={draftInputClass()}
          value={newRowDraft.locationCity}
          placeholder="City"
          onChange={(e) => updateNewRowField('locationCity', e.target.value)}
        />
      </td>
      <td className="px-2 py-1 min-w-[6rem]">
        <input
          className={draftInputClass()}
          value={newRowDraft.ethnicity}
          placeholder="Ethnicity"
          onChange={(e) => updateNewRowField('ethnicity', e.target.value)}
        />
      </td>
      <td className="px-2 py-1">
        <Select
          value={newRowDraft.gender}
          onChange={(e) => updateNewRowField('gender', e.target.value as KolGender | '')}
          className="text-sm py-1"
        >
          {GENDER_OPTIONS.map((o) => (
            <option key={o.value || 'unset'} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </td>
      <td className="px-2 py-1 min-w-[6rem]">
        <input
          className={draftInputClass()}
          value={newRowDraft.instagramHandle}
          placeholder="@handle"
          onChange={(e) => updateNewRowField('instagramHandle', e.target.value)}
        />
      </td>
      <td className="px-2 py-1 min-w-[5rem]">
        <input
          type="number"
          min={0}
          className={draftInputClass()}
          value={newRowDraft.instagramFollowers}
          placeholder="0"
          onChange={(e) => updateNewRowField('instagramFollowers', e.target.value)}
        />
      </td>
      <td className="px-2 py-1 min-w-[6rem]">
        <input
          className={draftInputClass()}
          value={newRowDraft.tiktokHandle}
          placeholder="@handle"
          onChange={(e) => updateNewRowField('tiktokHandle', e.target.value)}
        />
      </td>
      <td className="px-2 py-1 min-w-[5rem]">
        <input
          type="number"
          min={0}
          className={draftInputClass()}
          value={newRowDraft.tiktokFollowers}
          placeholder="0"
          onChange={(e) => updateNewRowField('tiktokFollowers', e.target.value)}
        />
      </td>
      <td className="px-2 py-1 min-w-[7rem]">
        <input
          className={draftInputClass()}
          value={newRowDraft.youtubeChannel}
          placeholder="Channel"
          onChange={(e) => updateNewRowField('youtubeChannel', e.target.value)}
        />
      </td>
      <td className="px-2 py-1 min-w-[5rem]">
        <input
          type="number"
          min={0}
          className={draftInputClass()}
          value={newRowDraft.youtubeFollowers}
          placeholder="0"
          onChange={(e) => updateNewRowField('youtubeFollowers', e.target.value)}
        />
      </td>
      <td className="px-2 py-1 min-w-[5rem]">
        <input
          type="number"
          min={0}
          className={draftInputClass()}
          value={newRowDraft.facebookFollowers}
          placeholder="0"
          onChange={(e) => updateNewRowField('facebookFollowers', e.target.value)}
        />
      </td>
      <td className="px-2 py-1 min-w-[6rem]">
        <input
          className={draftInputClass()}
          value={newRowDraft.wechatId}
          placeholder="WeChat ID"
          onChange={(e) => updateNewRowField('wechatId', e.target.value)}
        />
      </td>
      <td className="px-2 py-1 whitespace-nowrap text-xs text-glass-text-3">After save</td>
    </tr>
  ) : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/agency/kols')} aria-label="Back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="page-title">KOL roster table</h1>
            <p className="text-glass-text-2 mt-1 text-sm">
              Click a cell to edit, or use <strong className="text-glass-text">Add New Row</strong> to create a KOL
              inline. Name and email are required before saving.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link to="/agency/kols">
            <Button variant="outline">Back to search view</Button>
          </Link>
          <Button onClick={startNewRow} disabled={!agencyOrgId || !!newRowDraft || createMutation.isPending}>
            <Plus className="w-4 h-4 mr-2" />
            Add New Row
          </Button>
        </div>
      </div>

      {!agencyOrgId && !meQuery.isPending && (
        <div className="glass-card p-4 text-glass-text-2 text-sm">
          No agency membership found. Publish or assign your agency to use this page.
        </div>
      )}

      <div className="glass-card p-4 flex flex-wrap gap-4 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs text-glass-text-3 mb-1">Search name or email</label>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            disabled={!agencyOrgId}
          />
        </div>
        <div className="w-56 min-w-[180px]">
          <label className="block text-xs text-glass-text-3 mb-1">Tag filter</label>
          <Select
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            disabled={!agencyOrgId}
          >
            <option value="">All tags</option>
            {tags.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="text-sm text-glass-text-2">
          {agencyOrgId ? (
            <>
              {total.toLocaleString()} KOL{total === 1 ? '' : 's'}
              {listQuery.isFetching ? ' · Loading…' : ''}
            </>
          ) : null}
        </div>
      </div>

      {newRowError && (
        <div className="glass-card border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-500 dark:text-rose-300">
          {newRowError}
        </div>
      )}

      <div className="glass-card overflow-x-auto">
        {listQuery.isPending && !newRowDraft ? (
          <div className="p-12 text-center text-glass-text-2">Loading roster…</div>
        ) : !agencyOrgId ? (
          <div className="p-12 text-center text-glass-text-2">—</div>
        ) : showTable ? (
          <table className="min-w-[1520px] w-full divide-y divide-glass text-sm">
            {tableHeader}
            <tbody className="divide-y divide-glass bg-transparent">
              {newRowCells}
              {items.length === 0 && !newRowDraft ? (
                <tr>
                  <td colSpan={16} className="px-4 py-10 text-center text-glass-text-2">
                    No KOLs match your filters. Click <strong className="text-glass-text">Add New Row</strong> to
                    create one.
                  </td>
                </tr>
              ) : (
                items.map((kol) => (
                <tr key={kol.id} className="hover:bg-glass-2/40">
                  <td className="px-2 py-1 whitespace-nowrap">
                    <Link
                      to={`/agency/kols/${kolUrlSegment(kol)}/edit`}
                      className="inline-flex items-center gap-1 text-accent2 hover:underline text-xs font-medium"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                      Edit
                    </Link>
                  </td>
                  <td className="px-2 py-1 min-w-[7rem]">
                    <EditableTextCell
                      value={kol.name}
                      disabled={patchMutation.isPending}
                      onCommit={(next) => {
                        if (next == null || next.trim() === '') return;
                        commitField(kol.id, { name: next.trim() });
                      }}
                    />
                  </td>
                  <td className="px-2 py-1 min-w-[10rem]">
                    <EditableTextCell
                      value={kol.email}
                      disabled={patchMutation.isPending}
                      placeholder="email"
                      onCommit={(next) =>
                        commitField(kol.id, { email: next === undefined || next === '' ? null : next })
                      }
                    />
                  </td>
                  <td className="px-2 py-1 min-w-[7rem]">
                    <EditableTextCell
                      value={kol.phone}
                      disabled={patchMutation.isPending}
                      onCommit={(next) => commitField(kol.id, { phone: next })}
                    />
                  </td>
                  <td className="px-2 py-1 min-w-[7rem]">
                    <EditableTextCell
                      value={kol.locationCity}
                      disabled={patchMutation.isPending}
                      onCommit={(next) => commitField(kol.id, { locationCity: next })}
                    />
                  </td>
                  <td className="px-2 py-1 min-w-[6rem]">
                    <EditableTextCell
                      value={kol.ethnicity}
                      disabled={patchMutation.isPending}
                      onCommit={(next) => commitField(kol.id, { ethnicity: next })}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <Select
                      value={kol.gender ?? ''}
                      onChange={(e) => {
                        const v = e.target.value as KolGender | '';
                        const next = v === '' ? null : v;
                        if (next === kol.gender) return;
                        if (next === null && kol.gender == null) return;
                        commitField(kol.id, {
                          gender: next,
                        });
                      }}
                      disabled={patchMutation.isPending}
                      className="text-sm py-1"
                    >
                      {GENDER_OPTIONS.map((o) => (
                        <option key={o.value || 'unset'} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Select>
                  </td>
                  <td className="px-2 py-1 min-w-[6rem]">
                    <EditableTextCell
                      value={kol.instagramHandle}
                      disabled={patchMutation.isPending}
                      onCommit={(next) => commitField(kol.id, { instagramHandle: next })}
                    />
                  </td>
                  <td className="px-2 py-1 min-w-[5rem]">
                    <EditableNumberCell
                      value={kol.instagramFollowers}
                      disabled={patchMutation.isPending}
                      onCommit={(next) => commitField(kol.id, { instagramFollowers: next })}
                    />
                  </td>
                  <td className="px-2 py-1 min-w-[6rem]">
                    <EditableTextCell
                      value={kol.tiktokHandle}
                      disabled={patchMutation.isPending}
                      onCommit={(next) => commitField(kol.id, { tiktokHandle: next })}
                    />
                  </td>
                  <td className="px-2 py-1 min-w-[5rem]">
                    <EditableNumberCell
                      value={kol.tiktokFollowers}
                      disabled={patchMutation.isPending}
                      onCommit={(next) => commitField(kol.id, { tiktokFollowers: next })}
                    />
                  </td>
                  <td className="px-2 py-1 min-w-[7rem]">
                    <EditableTextCell
                      value={kol.youtubeChannel}
                      disabled={patchMutation.isPending}
                      onCommit={(next) => commitField(kol.id, { youtubeChannel: next })}
                    />
                  </td>
                  <td className="px-2 py-1 min-w-[5rem]">
                    <EditableNumberCell
                      value={kol.youtubeFollowers}
                      disabled={patchMutation.isPending}
                      onCommit={(next) => commitField(kol.id, { youtubeFollowers: next })}
                    />
                  </td>
                  <td className="px-2 py-1 min-w-[5rem]">
                    <EditableNumberCell
                      value={kol.facebookFollowers}
                      disabled={patchMutation.isPending}
                      onCommit={(next) => commitField(kol.id, { facebookFollowers: next })}
                    />
                  </td>
                  <td className="px-2 py-1 min-w-[6rem]">
                    <EditableTextCell
                      value={kol.wechatId}
                      disabled={patchMutation.isPending}
                      onCommit={(next) => commitField(kol.id, { wechatId: next })}
                    />
                  </td>
                  <td className="px-2 py-1 whitespace-nowrap">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      onClick={() => openTagsModal(kol)}
                      disabled={patchMutation.isPending}
                    >
                      <Tags className="w-3.5 h-3.5 mr-1" />
                      Tags
                      {kol.tags?.length ? ` (${kol.tags.length})` : ''}
                    </Button>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        ) : null}
      </div>

      {agencyOrgId && total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-glass-text-2">
          <span>
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1 || listQuery.isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages || listQuery.isFetching}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <Modal
        isOpen={!!tagsModalKol}
        onClose={() => setTagsModalKol(null)}
        title={tagsModalKol ? `Tags — ${tagsModalKol.name}` : 'Tags'}
        size="lg"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setTagsModalKol(null)}>
              Cancel
            </Button>
            <Button onClick={() => void saveTags()}>Save tags</Button>
          </div>
        }
      >
        <TagSelector tags={tags} selectedTagIds={tagsDraft} onChange={setTagsDraft} label="Categories" />
      </Modal>
    </div>
  );
}
